import { useState, useEffect } from "react";
import { trackEvent } from "@/lib/analytics";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { UserProfile } from "@/lib/storage";
import PersonalInfoStep from "./PersonalInfoStep";
import ResumeUploadStep from "./ResumeUploadStep";
import ExperienceStep from "./ExperienceStep";
import EducationStep from "./EducationStep";
import SkillsStep from "./SkillsStep";
import CommonQuestionsStep from "./CommonQuestionsStep";
import { ArrowLeft, ArrowRight, Check, Sparkles } from "lucide-react";

interface Props {
  profile: UserProfile;
  onUpdate: (profile: UserProfile) => void;
  onComplete: () => void;
}

const STEPS = ['Personal', 'Resume', 'Experience', 'Education', 'Skills', 'Questions'];

export default function OnboardingFlow({ profile, onUpdate, onComplete }: Props) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    trackEvent("onboarding_started");
  }, []);

  const handleComplete = () => {
    trackEvent("onboarding_completed");
    onComplete();
  };

  const renderStep = () => {
    switch (step) {
      case 0: return <PersonalInfoStep profile={profile} onChange={onUpdate} />;
      case 1: return <ResumeUploadStep profile={profile} onChange={onUpdate} />;
      case 2: return <ExperienceStep profile={profile} onChange={onUpdate} />;
      case 3: return <EducationStep profile={profile} onChange={onUpdate} />;
      case 4: return <SkillsStep profile={profile} onChange={onUpdate} />;
      case 5: return <CommonQuestionsStep profile={profile} onChange={onUpdate} />;
      default: return null;
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Welcome banner on first step */}
      {step === 0 && (
        <div className="px-5 py-5 bg-gradient-to-br from-primary to-primary/80 text-primary-foreground">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5" />
            <span className="font-display font-bold text-base">Welcome to Jobily!</span>
          </div>
          <p className="text-sm opacity-90">Set up your profile once, apply everywhere effortlessly.</p>
        </div>
      )}

      {/* Progress */}
      <div className="px-5 pt-4 pb-2">
        <div className="flex gap-1.5">
          {STEPS.map((s, i) => (
            <div key={s} className="flex-1 flex flex-col items-center gap-1">
              <div className={`h-1.5 w-full rounded-full transition-colors ${i <= step ? 'bg-primary' : 'bg-border'}`} />
              <span className={`text-[10px] font-medium ${i <= step ? 'text-primary' : 'text-muted-foreground'}`}>{s}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Step content */}
      <div className="flex-1 px-5 py-4 overflow-y-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation */}
      <div className="px-5 py-4 border-t border-border flex gap-3">
        {step > 0 && (
          <Button variant="outline" onClick={() => setStep(s => s - 1)} className="gap-1.5">
            <ArrowLeft className="w-4 h-4" /> Back
          </Button>
        )}
        <Button
          className="flex-1 gap-1.5"
          onClick={() => {
            if (step < STEPS.length - 1) setStep(s => s + 1);
            else handleComplete();
          }}
        >
          {step < STEPS.length - 1 ? (
            <>Next <ArrowRight className="w-4 h-4" /></>
          ) : (
            <>Complete Setup <Check className="w-4 h-4" /></>
          )}
        </Button>
      </div>
    </div>
  );
}
